import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import RadioButton from './RadioButton'

/**
 * RadioGroup component for managing a group of radio buttons
 * 
 * @param {Object} props - Component props
 * @param {string} props.name - Radio group name
 * @param {Array} props.options - Array of option objects with id, value, and label
 * @param {*} props.value - Currently selected value
 * @param {Function} props.onChange - Handler for value changes
 * @param {string} props.label - Optional group label
 * @param {boolean} props.inline - Whether to display radio buttons inline
 * @param {boolean} props.disabled - Whether all radio buttons are disabled
 * @param {string} props.className - Additional CSS classes
 */
const RadioGroup = ({
  name,
  options,
  value,
  onChange,
  label,
  inline = false,
  disabled = false,
  className = ''
}) => {
  const handleChange = useCallback(event => {
    const newValue = event.target.value
    // Convert to appropriate type if needed
    const option = options.find(opt => String(opt.value) === newValue)
    onChange(option ? option.value : newValue)
  }, [options, onChange])

  return (
    <div className={`form-group ${className}`}>
      {label ? <label className="font-weight-bold d-block">{label}</label> : null}
      <div className={inline ? 'd-flex' : ''}>
        {options.map((option, index) => (
          <RadioButton
            key={option.id || `${name}_${index}`}
            name={name}
            id={option.id || `${name}_${index}`}
            value={option.value}
            checked={value === option.value}
            label={option.label}
            disabled={disabled || option.disabled}
            className={inline ? 'mr-3' : ''}
            onChange={handleChange}
          />
        ))}
      </div>
    </div>
  )
}

RadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool
      ]).isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool
    })
  ).isRequired
}

export default RadioGroup